import UITabBar from 'UIComponents/nav/UITabBar';
import EditorMenuBarButton from 'automation-ui-canvas/components/flow-header/menu-bar/EditorMenuBarButton';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { generatePath, matchPath, useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { useGetHasScope, useGetIsUngated } from 'automation-ui-core/hooks/authHooks';
import { getIsClassicWorkflow } from 'automation-ui-canvas/flow/records/Flow/flowUtils';
import { useDispatch, useSelector } from 'react-redux';
import { Map as ImmutableMap } from 'immutable';
import { EditorQuickOptionButton } from 'automation-ui-canvas/components/flow-header/menu-bar/EditorQuickOptionButton';
import ErrorBoundary from 'automation-ui-core/errors/ErrorBoundary';
import { CANVAS_ROOT_TEST, CANVAS_ROOT_WORKFLOW } from 'automation-ui-canvas/constants/AbsolutePaths';
import { useShortcuts } from 'automation-ui-canvas/components/flow-header/menu-bar/useShortcuts';
import UIConfirmModal from 'UIComponents/dialog/UIConfirmModal';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { getOpenId, getIsActive, getModalState } from 'automation-ui-canvas/selectors/menuBarSelectors';
import { setOpenId, setIsActive, setModalStateById as setModalStateByIdAction } from 'automation-ui-canvas/actions/MenuBarActions';
import { unstable_batchedUpdates } from 'react-dom';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const OPEN_QUERY_PARAM = 'panel-open';
export const REDIRECT_TO_PANEL = 'redirect-to-panel';
const MenuBar = ({
  flow,
  canvasScrollElementRef,
  topLevelMenuOptions,
  allMenuOptionsById,
  shortcutOptions,
  extraMenuOptions,
  onMenuOptionClick
}) => {
  var _modalState$get;
  const openId = useSelector(getOpenId);
  const [isDirty, setIsDirty] = useState(false);
  const [onConfirm, setOnConfirm] = useState(() => () => {});
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const modalState = useSelector(getModalState);

  /*
      Indicates an active menu bar which is a requirement for scan/seeking behavior.
      This is a behavior where the menu will remain open on hover, as is conventional in
      many UIs.
   */
  const isActive = useSelector(getIsActive);
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const getIsUngated = useGetIsUngated();
  const getHasScope = useGetHasScope();
  const isClassicWorkflow = Boolean(getIsClassicWorkflow(flow));
  const {
    params: {
      revisionId
    }
  } = useRouteMatch();
  const [modalComponentId, setModalComponentId] = useState();
  const handleMouseEnter = useCallback(({
    id
  }) => {
    if (isActive) {
      dispatch(setOpenId({
        openId: id
      }));
    }
  }, [isActive, dispatch]);
  const handleOpenChange = useCallback(({
    id,
    value
  }) => {
    dispatch(setOpenId({
      openId: value ? id : null
    }));
    dispatch(setIsActive({
      isActive: value
    }));
  }, [dispatch]);
  const handleOnClose = useCallback(({
    /*
      Allow children to delegate search string to begin from.
      For example, to remove panel specific query params
    */
    search = location.search
  } = {}) => {
    const close = () => {
      const newSearchParams = new URLSearchParams(search);
      newSearchParams.delete(OPEN_QUERY_PARAM);
      const redirectTo = newSearchParams.get(REDIRECT_TO_PANEL);
      if (redirectTo) {
        newSearchParams.delete(REDIRECT_TO_PANEL);
        newSearchParams.set(OPEN_QUERY_PARAM, redirectTo);
      }
      /* React only batches updates if they happen during a syncronous user event. But if the update is async, anything that happens within that handler does not get batched. We must handle this as a batch because closing the modal depends on the URL being up to date */
      unstable_batchedUpdates(() => {
        setModalComponentId(undefined);
        history.replace({
          search: newSearchParams.toString()
        });
      });
    };
    if (modalComponentId && isDirty) {
      setShowConfirmModal(true);
      setOnConfirm(() => close);
      return;
    }
    close();
  }, [history, location, isDirty, modalComponentId]);
  const handleOptionClick = useCallback((option, options) => {
    const swapPanels = () => {
      if (onMenuOptionClick) {
        onMenuOptionClick(option, options);
      }

      /* A click or shortcut trigger will close the modal if its already open */
      if (modalComponentId && option.id === modalComponentId) {
        handleOnClose();
        return;
      }
      if (option.ModalComponent) {
        /*
          This is a regrettable hack. Because testing is so deeply ingrained in router,
          and scheduled for update soon, there isnt a better way to ensure the test panel
          gets dismissed if another panel becomes active
        */
        if (matchPath(location.pathname, [CANVAS_ROOT_TEST])) {
          history.push(generatePath(CANVAS_ROOT_WORKFLOW, {
            flowId: flow.flowId
          }));
        }

        /* Open the modal via the open param */
        const search = new URLSearchParams(options && options.search ? options.search : location.search);
        search.set(OPEN_QUERY_PARAM, option.id);
        history.push({
          search: search.toString()
        });
      }
      if (option.onClick && flow) {
        option.onClick({
          history,
          flow,
          revisionId
        });
      }
    };

    /* If the state is dirty, we need to confirm before swapping panels */
    if (option.ModalComponent && isDirty) {
      setShowConfirmModal(true);
      setOnConfirm(() => swapPanels);
      return;
    }
    swapPanels();
  }, [history, flow, revisionId, location, modalComponentId, handleOnClose, isDirty, onMenuOptionClick]);
  useShortcuts({
    flow,
    onOptionClick: handleOptionClick,
    shortcutOptions
  });

  /* Open panels according to route query params */
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const openQueryParam = searchParams.get(OPEN_QUERY_PARAM);
    const selectedMenuItem = allMenuOptionsById.get(openQueryParam || '');
    if (openQueryParam && selectedMenuItem && selectedMenuItem.ModalComponent) {
      setModalComponentId(selectedMenuItem.id);
    }
    if (!openQueryParam && modalComponentId) {
      handleOnClose();
    }
  }, [location.search, history, allMenuOptionsById, handleOnClose, modalComponentId]);
  const selectedOption = allMenuOptionsById.get(modalComponentId || '');
  const setModalStateById = useMemo(() => {
    const prev = modalState.get(selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.id) || ImmutableMap();
    return value => {
      const newValue = value instanceof Function ? value(prev) : value;
      dispatch(setModalStateByIdAction({
        menuOptionId: selectedOption.id,
        modalState: newValue
      }));
    };
  }, [selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.id, modalState, dispatch]);
  if (!flow) {
    return null;
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [selectedOption && selectedOption.ModalComponent && /*#__PURE__*/_jsx("div", {
      "data-test-id": `modal-component-${selectedOption.id}`,
      children: /*#__PURE__*/_jsx(selectedOption.ModalComponent, {
        canvasScrollElementRef: canvasScrollElementRef,
        flow: flow,
        onClose: handleOnClose,
        setActiveOption: handleOptionClick,
        setIsDirty: setIsDirty,
        topLevelMenuOptions: topLevelMenuOptions,
        modalState: (_modalState$get = modalState === null || modalState === void 0 ? void 0 : modalState.get(selectedOption.id)) !== null && _modalState$get !== void 0 ? _modalState$get : ImmutableMap(),
        setModalState: setModalStateById
      })
    }), showConfirmModal && /*#__PURE__*/_jsx(UIConfirmModal, {
      message: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "automation.common.closeConfirmation.message"
      }),
      description: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "automation.common.closeConfirmation.description"
      }),
      confirmLabel: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "automation.common.closeConfirmation.confirmLabel"
      }),
      onConfirm: () => {
        setShowConfirmModal(false);
        setIsDirty(false);
        onConfirm();
      },
      rejectLabel: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "automation.common.closeConfirmation.rejectLabel"
      }),
      onReject: () => {
        setShowConfirmModal(false);
        const search = new URLSearchParams(location.search);
        search.set(OPEN_QUERY_PARAM, selectedOption.id);
        history.push({
          search: search.toString()
        });
      },
      size: "auto"
    }), /*#__PURE__*/_jsx(UITabBar, {
      bordered: true,
      startSlot: /*#__PURE__*/_jsx(_Fragment, {
        children: topLevelMenuOptions.filter(option => !option.getIsVisible || option.getIsVisible({
          getIsUngated,
          getHasScope,
          isClassicWorkflow,
          flow
        })).map(option => /*#__PURE__*/_jsx(EditorMenuBarButton, {
          id: option.id,
          open: openId === option.id,
          disabled: option.disabled,
          options: option.options,
          title: option.getLabel({
            objectTypeId: flow.objectTypeId
          }),
          onMouseEnter: handleMouseEnter,
          onOpenChange: handleOpenChange,
          objectTypeId: flow.objectTypeId,
          onOptionClick: handleOptionClick,
          onClick: () => {
            handleOptionClick(option);
          },
          flow: flow,
          topLevelMenuOptions: topLevelMenuOptions
        }, option.id))
      }),
      endSlot: /*#__PURE__*/_jsx(_Fragment, {
        children: /*#__PURE__*/_jsx(ErrorBoundary, {
          boundaryName: "menuQuickOptions",
          renderError: () => null,
          children: extraMenuOptions ? extraMenuOptions.map(option => {
            return /*#__PURE__*/_jsx(EditorQuickOptionButton, {
              option: option,
              flow: flow,
              onOptionClick: handleOptionClick
            }, option.id);
          }) : null
        })
      })
    })]
  });
};
export default MenuBar;